import { EventEmitter, Injectable, Injector, inject } from '@angular/core';
import { BackOffice, Gateway, RoleCode } from 'package-types';
import { Api } from '../api/api.service';
import { Merchant, MerchantGroup } from 'src/app/models/Merchant';
import { SocketService } from '../socket/socket.service';
import { InstallLabelParts, LabelInstall, getInstallLabel, getInstallLabelParts } from 'src/app/utils/install-label';

export type User = Omit<BackOffice.SafeUserWithPermissions, 'merchantId'> & {
  merchantId?: string | null;
};
const ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY = 'boAccessTokenExpiresAt';
const AUTH_BLOCKED_STORAGE_KEY = 'boAuthBlocked';
const AUTH_SYNC_STORAGE_KEY = 'boAuthSyncEvent';
const ACCESS_TOKEN_REFRESH_BUFFER_MS = 20 * 1000;
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private injector = inject(Injector);

  public userData: User;
  public merchants: Merchant[] = [];
  public merchantsChange = new EventEmitter<Merchant[]>();
  public merchantGroups: MerchantGroup[] = [];
  /** a merchant is in one group at most */
  public merchantGroupByMerchantId: Record<string, MerchantGroup> = {};
  public merchantsById: Record<string, Merchant> = {};
  public loginEvent = new EventEmitter<void>();
  public authSyncEvent = new EventEmitter<'login' | 'logout'>();
  public userLoaded = new EventEmitter<void>();
  public roleCode = RoleCode;
  private userFetchPromise: Promise<void> | null = null;
  private userFetchFailed = false;
  private userFetchAttempted = false;
  private accessTokenExpiresAt: number | null = null;
  private refreshPromise: Promise<void> | null = null;
  private authBlocked = false;

  private socketServiceRef: SocketService | null = null;

  constructor() {
    const storedExpiresAt = localStorage.getItem(ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY);
    if (storedExpiresAt) {
      const parsed = Number(storedExpiresAt);
      this.accessTokenExpiresAt = Number.isFinite(parsed) ? parsed : null;
    }
    this.authBlocked = localStorage.getItem(AUTH_BLOCKED_STORAGE_KEY) === 'true';
    window.addEventListener('storage', (event) => {
      if (event.key === AUTH_BLOCKED_STORAGE_KEY) {
        this.authBlocked = event.newValue === 'true';
      }
      if (event.key === ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY) {
        const parsed = Number(event.newValue);
        this.accessTokenExpiresAt = Number.isFinite(parsed) ? parsed : null;
      }
      if (event.key === AUTH_SYNC_STORAGE_KEY && event.newValue) {
        const payload = this.parseAuthSyncPayload(event.newValue);
        if (!payload) {
          return;
        }
        if (payload.type === 'logout') {
          this.logoutUser(false, false);
        }
        if (payload.type === 'login') {
          this.handleRemoteLogin();
        }
        this.authSyncEvent.emit(payload.type);
      }
    });
  }

  public async loginUser(credential: Gateway.Actions.BoLoginArguments['params']) {
    const userData = await Api.login({
      ...credential,
      ...{ appName: Gateway.AppNames.FridgeServiceMan },
    });
    this.authBlocked = false;
    localStorage.setItem(AUTH_BLOCKED_STORAGE_KEY, 'false');
    this.setAccessTokenExpiresAt(this.getExpiresAtFromResult(userData));
    this.setUserData(userData.user);
    this.loginEvent.emit();
    this.broadcastAuthSync('login');
  }

  public async logout() {
    try {
      await Api.logoutBo();
    } catch (e) {
      // Ignore server logout failures; local state still needs to be cleared.
    } finally {
      this.logoutUser(true, true);
    }
  }

  public logoutUser(resetAuthFailed: boolean = true, broadcastSync: boolean = false) {
    this.merchants = [];
    this.merchantsById = {};
    this.merchantGroups = [];
    this.merchantGroupByMerchantId = {};
    this.userData = null;
    this.userFetchPromise = null;
    this.refreshPromise = null;
    this.authBlocked = true;
    localStorage.setItem(AUTH_BLOCKED_STORAGE_KEY, 'true');
    this.setAccessTokenExpiresAt(null);
    if (resetAuthFailed) {
      this.userFetchFailed = false;
      this.userFetchAttempted = false;
    }
    this.socketService.teardown();
    if (broadcastSync) {
      this.broadcastAuthSync('logout');
    }
  }

  public setUserData(data: User) {
    this.userData = data;
    this.userFetchFailed = false;
    this.userFetchAttempted = false;
    this.userLoaded.emit();
  }

  public get isAuthenticated(): boolean {
    return !!this.userData;
  }

  public get hasAuthFailed(): boolean {
    return this.userFetchFailed;
  }

  public async ensureUserLoaded(): Promise<void> {
    if (this.userData) {
      return;
    }
    if (this.userFetchFailed || this.userFetchAttempted) {
      throw new Error('auth_failed');
    }
    if (this.userFetchPromise) {
      return this.userFetchPromise;
    }
    this.userFetchAttempted = true;
    this.userFetchPromise = (async () => {
      try {
        const { user } = await Api.getUserByToken();
        this.setUserData(user);
      } catch (e) {
        this.userFetchFailed = true;
        throw e;
      }
    })();
    try {
      await this.userFetchPromise;
    } finally {
      this.userFetchPromise = null;
    }
  }

  public async ensureValidAccessToken(): Promise<void> {
    this.syncAuthBlockedFromStorage();
    this.syncExpiresAtFromStorage();
    if (this.authBlocked) {
      throw new Error('auth_blocked');
    }
    if (!this.accessTokenExpiresAt) {
      return;
    }
    const now = Date.now();
    if (now + ACCESS_TOKEN_REFRESH_BUFFER_MS < this.accessTokenExpiresAt) {
      return;
    }
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    this.refreshPromise = (async () => {
      try {
        const refreshResult = await Api.refreshBoAccessToken();
        this.setAccessTokenExpiresAt(this.getExpiresAtFromResult(refreshResult));
      } catch (e) {
        this.setAccessTokenExpiresAt(null);
        this.authBlocked = true;
        localStorage.setItem(AUTH_BLOCKED_STORAGE_KEY, 'true');
        this.logoutUser(false, true);
        throw e;
      }
    })();
    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  public get isRoot() {
    return this.userData.roles?.includes(RoleCode.Root);
  }

  /** @deprecated use isInternal instead */
  public get haveMerchant() {
    return !!this.userData.merchantId;
  }

  public get isInternal() {
    return this.userData.type === 'internal';
  }

  public getMerchantById(merId: string): Merchant | undefined {
    return this.merchantsById[merId];
  }

  /** Full fridge label, the same as in back-office-board (see utils/install-label). */
  public fridgeLabel(fridge: (LabelInstall & { merchantId?: unknown }) | null | undefined): string {
    if (!fridge) return '';
    return getInstallLabel(fridge, this.getMerchantById(fridge.merchantId as string), {
      isInternal: !!this.userData && this.isInternal,
    });
  }

  public fridgeLabelParts(fridge: (LabelInstall & { merchantId?: unknown }) | null | undefined): InstallLabelParts {
    return getInstallLabelParts(fridge, fridge ? this.getMerchantById(fridge.merchantId as string) : undefined);
  }

  /** @deprecated */
  public userMerchant(): Merchant | null | undefined {
    if (!this.userData.merchantId) {
      return null;
    }

    return this.getMerchantById(this.userData.merchantId);
  }

  public getUserData() {
    return this.userData;
  }

  public setMerchants(merchants: Merchant[]) {
    this.merchants = merchants;
    this.merchantsById = Object.fromEntries(merchants.map((m) => [m._id, m]));
    this.merchantsChange.emit(this.merchants);
  }

  /** Groups only order the fridge list; emits merchantsChange so the list regroups. */
  public setMerchantGroups(merchantGroups: MerchantGroup[]) {
    this.merchantGroups = merchantGroups;
    const byMerchantId: Record<string, MerchantGroup> = {};
    merchantGroups.forEach((group) => (group.merchantIds || []).forEach((id) => (byMerchantId[String(id)] = group)));
    this.merchantGroupByMerchantId = byMerchantId;
    this.merchantsChange.emit(this.merchants);
  }

  hasPermissionOrRoot(permission) {
    if (!this.userData) {
      return false;
    }
    return (
      this.userData.roles?.includes(RoleCode.Root) ||
      this.userData.permissions?.includes(permission)
    );
  }

  private getExpiresAtFromResult(result: { boAccessTokenTtl?: number }): number | null {
    if (typeof result?.boAccessTokenTtl === 'number') {
      return Date.now() + result.boAccessTokenTtl;
    }
    return null;
  }

  private setAccessTokenExpiresAt(expiresAt: number | null) {
    if (!expiresAt) {
      this.accessTokenExpiresAt = null;
      localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY);
      return;
    }
    if (!Number.isFinite(expiresAt)) {
      this.accessTokenExpiresAt = null;
      localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY);
      return;
    }
    this.accessTokenExpiresAt = expiresAt;
    localStorage.setItem(ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY, String(expiresAt));
  }

  private syncAuthBlockedFromStorage(): void {
    this.authBlocked = localStorage.getItem(AUTH_BLOCKED_STORAGE_KEY) === 'true';
  }

  private syncExpiresAtFromStorage(): void {
    const storedExpiresAt = localStorage.getItem(ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY);
    if (!storedExpiresAt) {
      this.accessTokenExpiresAt = null;
      return;
    }
    const parsed = Number(storedExpiresAt);
    this.accessTokenExpiresAt = Number.isFinite(parsed) ? parsed : null;
  }

  private broadcastAuthSync(type: 'login' | 'logout'): void {
    const payload = JSON.stringify({ type, ts: Date.now() });
    localStorage.setItem(AUTH_SYNC_STORAGE_KEY, payload);
  }

  private parseAuthSyncPayload(value: string): { type: 'login' | 'logout' } | null {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.type === 'login' || parsed?.type === 'logout') {
        return { type: parsed.type };
      }
    } catch (e) {
      return null;
    }
    return null;
  }

  private handleRemoteLogin(): void {
    this.authBlocked = false;
    this.userFetchFailed = false;
    this.userFetchAttempted = false;
    this.userFetchPromise = null;
    this.syncAuthBlockedFromStorage();
    this.syncExpiresAtFromStorage();
  }

  private get socketService(): SocketService {
    if (!this.socketServiceRef) {
      this.socketServiceRef = this.injector.get(SocketService);
    }
    return this.socketServiceRef;
  }
}
